import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageModal from '../components/PageModal';
import { CreditCard, Smartphone, Building, User, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart, getCartCount, discount, getDiscountAmount, getFinalTotal } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('paytr');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutMode, setCheckoutMode] = useState(null); // null, 'member', 'guest'
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [agreeContract, setAgreeContract] = useState(false);
  const [modalPage, setModalPage] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    neighborhood: '',
    street: '',
    avenue: '',
    apartmentNo: '',
    address: '',
    city: '',
    zipCode: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('userToken');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      setIsLoggedIn(true);
      setCheckoutMode('member'); // Auto-set to member mode
      const userData = JSON.parse(user);
      setFormData(prev => ({
        ...prev,
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || ''
      }));
    }
  }, []);

  // Save abandoned cart when user fills email (debounced 2s)
  useEffect(() => {
    if (!formData.email || !formData.email.includes('@') || cartItems.length === 0) return;
    
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/abandoned-cart/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          first_name: formData.firstName || '',
          items: cartItems.map(item => ({
            product_id: item.id,
            name: item.name,
            price: parseFloat(item.price),
            quantity: item.quantity,
            subtotal: parseFloat(item.price) * item.quantity
          })),
          total: parseFloat(getCartTotal())
        })
      }).catch(() => {}); // Silent fail - non-critical
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [formData.email, formData.firstName, cartItems, getCartTotal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const MAX_QUANTITY_PER_PRODUCT = 3;
    const MAX_CART_ITEMS = 10;
    
    // Check if cart is empty
    if (cartItems.length === 0) {
      alert('Sepetiniz boş! Lütfen ürün ekleyin.');
      navigate('/products');
      return;
    }

    // Business rules validation
    const issues = [];
    let totalQuantity = 0;
    
    for (const item of cartItems) {
      totalQuantity += item.quantity;
      
      // Per-product limit check
      if (item.quantity > MAX_QUANTITY_PER_PRODUCT) {
        issues.push(`${item.name} - Her üründen maksimum ${MAX_QUANTITY_PER_PRODUCT} adet sipariş verebilirsiniz (Şu an: ${item.quantity} adet)`);
      }
      
      // Stock check
      if (item.stock !== undefined) {
        if (item.stock === 0) {
          issues.push(`${item.name} stokta yok`);
        } else if (item.quantity > item.stock) {
          issues.push(`${item.name} için maksimum ${item.stock} adet sipariş verebilirsiniz`);
        }
      }
    }
    
    // Total cart limit check
    if (totalQuantity > MAX_CART_ITEMS) {
      issues.push(`Sepetinizde maksimum ${MAX_CART_ITEMS} ürün olabilir (Şu an: ${totalQuantity} ürün)`);
    }

    if (issues.length > 0) {
      alert('Sipariş Uyarısı:\n\n' + issues.join('\n\n') + '\n\nLütfen sepetinizi kontrol edin.');
      return;
    }

    if (!agreeContract) {
      alert('Siparişi tamamlamak için Mesafeli Satış Sözleşmesi\'ni ve Ön Bilgilendirme Formu\'nu kabul etmelisiniz.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare order data matching backend model
      const subtotalVal = parseFloat(getCartTotal());
      const discountAmt = parseFloat(getDiscountAmount());
      const finalTotal = parseFloat(getFinalTotal());
      
      // Build a single address string from the structured fields for the order record.
      const addrParts = [
        formData.neighborhood && `${formData.neighborhood} Mah.`,
        formData.avenue && `${formData.avenue} Cad.`,
        formData.street && `${formData.street} Sok.`,
        formData.apartmentNo && `No: ${formData.apartmentNo}`,
      ].filter(Boolean);
      const composedAddress = addrParts.join(' ').trim() || formData.address;

      const orderData = {
        customer_info: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: composedAddress,
          neighborhood: formData.neighborhood || '',
          avenue: formData.avenue || '',
          street: formData.street || '',
          apartment_no: formData.apartmentNo || '',
          city: formData.city,
          zip_code: formData.zipCode || ''
        },
        items: cartItems.map(item => ({
          product_id: parseInt(item.id) || 0,
          product_name: item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          subtotal: parseFloat(item.price) * item.quantity
        })),
        subtotal: subtotalVal,
        discount_code: discount?.code || null,
        discount_amount: discountAmt,
        total: finalTotal,
        payment_method: paymentMethod
      };

      // Send order to backend
      const response = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error('Sipariş oluşturulamadı. Lütfen tekrar deneyin.');
      }

      const result = await response.json();
      
      // Save email hint for payment status polling and order lookup
      try {
        window.__lastOrderEmail = formData.email;
        sessionStorage.setItem('lastOrderEmail', formData.email);
      } catch (_) {}

      // Route to payment page based on chosen provider (PayTR or Iyzico)
      if (paymentMethod === 'card' || paymentMethod === 'credit_card' ||
          paymentMethod === 'paytr' || paymentMethod === 'iyzico') {
        const provider = (paymentMethod === 'iyzico') ? 'iyzico' : 'paytr';
        navigate(`/payment/${result.order_id}?provider=${provider}`);
        return;
      }

      // For other methods (havale, kapıda ödeme): old success flow
      clearCart();
      navigate(`/order-success/${result.order_id}`, { 
        state: { 
          orderData: result,
          customerInfo: formData
        } 
      });
      
    } catch (err) {
      console.error('Sipariş hatası:', err);
      setError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      alert('Sipariş oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const cartTotal = getCartTotal();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8">Ödeme</h1>

        {/* Guest vs Member Selection - Only show if not logged in and mode not selected */}
        {!isLoggedIn && checkoutMode === null && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6 text-center">Nasıl Devam Etmek İstersiniz?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Login Option */}
                <button
                  type="button"
                  onClick={() => navigate('/login', { state: { from: '/checkout' } })}
                  className="flex flex-col items-center gap-4 p-6 border-2 border-gray-300 rounded-lg hover:border-red-600 hover:bg-red-50 transition-all group"
                >
                  <User size={48} className="text-red-600" />
                  <div className="text-center">
                    <h3 className="font-bold text-lg mb-2">Üye Girişi Yap</h3>
                    <p className="text-sm text-gray-600">
                      Hesabınıza giriş yaparak siparişlerinizi takip edin
                    </p>
                  </div>
                  <span className="text-red-600 font-semibold group-hover:underline">
                    Giriş Yap →
                  </span>
                </button>

                {/* Guest Checkout Option */}
                <button
                  type="button"
                  onClick={() => setCheckoutMode('guest')}
                  className="flex flex-col items-center gap-4 p-6 border-2 border-gray-300 rounded-lg hover:border-red-600 hover:bg-red-50 transition-all group"
                >
                  <ShoppingCart size={48} className="text-red-600" />
                  <div className="text-center">
                    <h3 className="font-bold text-lg mb-2">Misafir Olarak Devam Et</h3>
                    <p className="text-sm text-gray-600">
                      Üye olmadan hızlıca alışverişinizi tamamlayın
                    </p>
                  </div>
                  <span className="text-red-600 font-semibold group-hover:underline">
                    Devam Et →
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show form only if checkout mode is selected */}
        {(checkoutMode === 'member' || checkoutMode === 'guest') && (
          <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">Teslimat Bilgileri</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ad <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Soyad <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mahalle <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleChange}
                      required
                      placeholder="Örn: Çankaya"
                      data-testid="checkout-neighborhood"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cadde <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="avenue"
                      value={formData.avenue}
                      onChange={handleChange}
                      required
                      placeholder="Örn: Atatürk"
                      data-testid="checkout-avenue"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sokak
                    </label>
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Örn: Gül"
                      data-testid="checkout-street"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bina / Daire No <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="apartmentNo"
                      value={formData.apartmentNo}
                      onChange={handleChange}
                      required
                      placeholder="Örn: 12 / 5"
                      data-testid="checkout-apartment-no"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Şehir <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Posta Kodu
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">Ödeme Yöntemi</h2>
                <div className="space-y-3 mb-6">
                  <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    paymentMethod === 'paytr' || paymentMethod === 'card'
                      ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-red-300'
                  }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="paytr"
                      checked={paymentMethod === 'paytr' || paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      data-testid="payment-method-paytr"
                      className="accent-red-600"
                    />
                    <CreditCard className="text-red-600" size={24} />
                    <div className="flex-1">
                      <span className="font-medium block">PayTR ile Güvenli Ödeme</span>
                      <span className="text-xs text-gray-500">Kredi/Banka Kartı &amp; EFT/Havale</span>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    paymentMethod === 'iyzico' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="iyzico"
                      checked={paymentMethod === 'iyzico'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      data-testid="payment-method-iyzico"
                      className="accent-blue-600"
                    />
                    <CreditCard className="text-blue-600" size={24} />
                    <div className="flex-1">
                      <span className="font-medium block">Iyzico ile Güvenli Ödeme</span>
                      <span className="text-xs text-gray-500">Kredi/Banka Kartı &amp; Taksit Seçenekleri</span>
                    </div>
                  </label>
                </div>

                <div className="space-y-2 pt-4 border-t bg-emerald-50 -mx-6 px-6 py-4 rounded-b-lg">
                  <p className="text-sm text-emerald-800 flex items-start gap-2">
                    <span className="text-lg leading-none">🔒</span>
                    <span>
                      Seçtiğiniz sağlayıcının <strong>3D Secure</strong> korumalı ödeme sayfasına yönlendirileceksiniz. Kart bilgileriniz sitemizde saklanmaz.
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
                <h2 className="text-xl font-bold mb-6">Sipariş Özeti</h2>
                
                {cartItems.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">Sepetiniz boş</p>
                    <button
                      type="button"
                      onClick={() => navigate('/products')}
                      className="text-red-600 hover:text-red-700 font-semibold"
                    >
                      Alışverişe Başla →
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-6">
                      <div className="border-b pb-3 mb-3">
                        {cartItems.map((item) => (
                          <div key={item.id} className="flex justify-between items-center py-2">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-gray-500">{item.quantity} adet</p>
                            </div>
                            <span className="text-sm font-semibold">
                              {(item.price * item.quantity).toLocaleString('tr-TR')} TL
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ara Toplam</span>
                        <span className="font-semibold">{cartTotal.toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kargo</span>
                        <span className="font-semibold text-green-600">Ücretsiz</span>
                      </div>
                      {discount && (
                        <div className="flex justify-between bg-green-50 p-2 rounded">
                          <span className="text-sm text-green-700">
                            İndirim ({discount.code} %{discount.percent})
                          </span>
                          <span className="font-semibold text-green-700">
                            -{getDiscountAmount().toLocaleString('tr-TR')} TL
                          </span>
                        </div>
                      )}
                      <div className="border-t pt-3 flex justify-between">
                        <span className="text-lg font-bold">Toplam</span>
                        <span className="text-lg font-bold text-red-600">
                          {getFinalTotal().toLocaleString('tr-TR')} TL
                        </span>
                      </div>
                    </div>

                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreeContract}
                          onChange={(e) => setAgreeContract(e.target.checked)}
                          data-testid="checkout-agree-contract"
                          className="mt-1 w-4 h-4 accent-red-600 flex-shrink-0"
                        />
                        <span className="text-xs text-gray-700 leading-relaxed">
                          <button
                            type="button"
                            onClick={() => setModalPage({ slug: 'mesafeli-satis-sozlesmesi', title: 'Mesafeli Satış Sözleşmesi' })}
                            data-testid="checkout-open-contract-modal"
                            className="text-red-600 underline font-medium bg-transparent border-0 p-0 cursor-pointer"
                          >
                            Mesafeli Satış Sözleşmesi
                          </button>
                          {' '}ve{' '}
                          <button
                            type="button"
                            onClick={() => setModalPage({ slug: 'tuketici-haklari', title: 'Ön Bilgilendirme / Tüketici Hakları' })}
                            data-testid="checkout-open-preinfo-modal"
                            className="text-red-600 underline font-medium bg-transparent border-0 p-0 cursor-pointer"
                          >
                            Ön Bilgilendirme Formu
                          </button>
                          'nu okudum ve kabul ediyorum.
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || cartItems.length === 0}
                      data-testid="checkout-submit-btn"
                      className={`w-full py-3 rounded-lg transition-colors font-semibold mb-4 ${
                        loading || cartItems.length === 0
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {loading ? 'İşleniyor...' : 'Siparişi Tamamla'}
                    </button>

                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        Güvenli ödeme
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        14 gün iade garantisi
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        Ücretsiz kargo
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </form>
        )}
      </div>

      <Footer />
      
      {/* Page modal for legal docs */}
      {modalPage && (
        <PageModal
          slug={modalPage.slug}
          title={modalPage.title}
          onClose={() => setModalPage(null)}
        />
      )}
    </div>
  );
};

export default CheckoutPage;
